/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  of as just,
  map,
  shareReplay,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { DEFAULT_SEARCH_CHAR_LIMIT } from 'src/app/defaults/search.defaults';
import { environment } from 'src/environments/environment';
import { formatISO } from 'date-fns';
import {
  deepCopy,
  makeObjectWithNoNullValues,
} from '@app-shared/functions/helpers.functions';
import { FileTypes } from '@app-shared/enums/file-types.enum';
import { IProductViewsItemCounts } from 'src/app/entities/products/interfaces/product-views.interface';
import {
  getActiveTableColumns,
  parseTableColumnConfig,
} from 'src/app/entities/products/functions/table-columns.functions';
import { LanguageRepository } from '@app-core/state/language.repository';
import { AtcsRepository } from 'src/app/entities/filters/atc/state/atc.repository';
import { emitOnce } from '@ngneat/elf';
import { SessionRepository } from '@app-core/state/session.repository';

import { SortByOrder } from '../enums/sort-by-filters.enum';
import { IProduct } from './product.model';
import {
  ISearchData,
  ISearchQueryParams,
  ISortBySearchParam,
} from './search.model';
import { ITableColumnConfig } from '../interfaces/table-column.interface';
import {
  BackendViewNames,
  ProductViews,
  productViewToTabNameMap,
} from '../enums/product-views.enum';
import { ManufacturerRepository } from '../../filters/manufacturers/state/manufacturer.repository';
import { OtherFiltersRepository } from '../../filters/other-filters/state/other-filters.repository';
import { DataFieldsRepository } from '../../filters/data-fields/state/data-fields.repository';
import { MoleculesRepository } from '../../filters/molecules/state/molecules.repository';
import { CountryRepository } from '../../countries/state/country.repository';
import { ProductsRepository } from './products.repository';
import { CountriesFilterRepository } from '../../filters/countries/state/countries-filter.repository';

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private readonly countryRepo = inject(CountryRepository);
  private readonly atcsRepo = inject(AtcsRepository);
  private readonly manufacturersRepo = inject(ManufacturerRepository);
  private readonly moleculesRepo = inject(MoleculesRepository);
  private readonly dataFieldsRepo = inject(DataFieldsRepository);
  private readonly otherFiltersRepo = inject(OtherFiltersRepository);
  private readonly languageRepo = inject(LanguageRepository);
  private readonly productsRepo = inject(ProductsRepository);
  private readonly countriesFilterRepo = inject(CountriesFilterRepository);
  private readonly sessionRepo = inject(SessionRepository);

  private readonly http = inject(HttpClient);

  /** Subscription that calls the api that requires search term to work */
  private searchSubscription!: Subscription;
  private suggestionsSearchSubscription!: Subscription;

  /** Cancels requests to the api */
  private cancelRequest = new Subject<void>();
  private cancelSuggestionRequest = new Subject<void>();

  /**
   * We basically almost always want to reset the page and clear old results when a filter changes,
   * so all these subscriptions are grouped together to avoid code duplication.
   *
   * If a filter doesn't need to do this, it can be put separately like
   * the `currentPage$` in the `shouldFetch` subscriptions
   */
  private shouldResetPageAndClear$ = combineLatest([
    this.productsRepo.sortByFilter$,
    this.productsRepo.sortByOrder$,
    this.productsRepo.columnSortByFilter$,
    this.productsRepo.columnSortByOrder$,
    this.productsRepo.selectedProduct$,
    this.productsRepo.startDate$,
    this.productsRepo.shortageStatusFilter$,
    this.productsRepo.productView$,
    this.productsRepo.activeCustomersFilter$,
    this.productsRepo.activeSortalysTimePeriodsFilter$,

    this.manufacturersRepo.selectedManufacturers$,
    this.moleculesRepo.selectedMolecules$,
    this.atcsRepo.selectedAtcs$,
    this.countriesFilterRepo.selectedCountries$,

    this.languageRepo.activeLanguage$,

    this.dataFieldsRepo.otc$,
    this.dataFieldsRepo.btm$,
    this.dataFieldsRepo.tender$,

    this.otherFiltersRepo.manufacturerTypeEnabled$,
    this.otherFiltersRepo.manufacturerType$,
    this.otherFiltersRepo.expectedShortageDate$,

    this.countryRepo.activeCountryId$,
  ]);
  /** Emits next value when api should be called */
  readonly shouldFetch$ = combineLatest([
    this.productsRepo.searchTerm$.pipe(
      tap(() => this.clearCurrentPageIDs()),
      filter(() => this.firstPagePredicate()),
    ),
    this.productsRepo.currentPage$,
    this.shouldResetPageAndClear$.pipe(
      tap(() => this.clearCurrentPageIDs()),
      filter(() => this.firstPagePredicate()),
    ),
  ]).pipe(
    debounceTime(0),
    tap(() => this.productsRepo.setError(null)),
  );

  /** Emits next value when api should be called */
  readonly shouldFetchSuggestions$ = combineLatest([
    this.productsRepo.suggestionsSearchTerm$.pipe(
      tap(() => this.clearSuggestionIDs()),
      filter(() => this.firstPagePredicate()),
    ),
    this.productsRepo.currentPage$,
    this.shouldResetPageAndClear$.pipe(
      tap(() => this.clearSuggestionIDs()),
      filter(() => this.firstPagePredicate()),
    ),
  ]).pipe(
    debounceTime(0),
    tap(() => this.productsRepo.setError(null)),
  );

  private disclaimerTextSubscription!: Subscription;

  readonly shouldFetchDisclaimer$ = combineLatest([
    this.languageRepo.activeLanguage$,
    this.countryRepo.activeCountry$,
  ]);

  private downloading$ = new BehaviorSubject(false);

  /**
   * Resets page back to 1 if it is not already 1
   *
   * @returns true if it is first page
   */
  firstPagePredicate(): boolean {
    const isFirstPage = this.productsRepo.getStoreProp('currentPage') === 1;

    if (!isFirstPage) {
      this.setPage(1);
    }

    return isFirstPage;
  }

  private handleSearchData(searchData: ISearchData): void {
    this.productsRepo.upsertProducts(searchData.data);

    const dataMap = searchData.data.map(d => d.id);

    this.productsRepo.updateStoreProps({
      currentPage: searchData.pages.current,
      totalPages: searchData.pages.total,
      totalItems: searchData.total,
      nextPage: searchData.pages.next,
      hasInitFetch: true,
      currentPageIDs:
        searchData.pages.current > 1
          ? this.productsRepo.getStoreProp('currentPageIDs').concat(dataMap)
          : dataMap,
    });
  }

  private handleHttpError(err: HttpErrorResponse): void {
    this.productsRepo.setLoading(false);

    if (err.status === 404 && err.error && err.error.data && err.error.pages) {
      const searchData: ISearchData = err.error;

      this.productsRepo.updateStoreProps({
        currentPage: searchData.pages.current,
        totalPages: searchData.pages.total,
        totalItems: searchData.total,
        nextPage: searchData.pages.next,
        hasInitFetch: true,
        currentPageIDs: [],
      });
    } else {
      emitOnce(() => {
        this.productsRepo.setError(err);
        this.productsRepo.setStoreProp('hasInitFetch', true);
      });

      this.clearCurrentPageIDs();
    }
  }

  private handleSuggestionsSearchData(searchData: ISearchData): void {
    emitOnce(() => {
      this.productsRepo.upsertProducts(searchData.data);
      this.productsRepo.setStoreProp(
        'suggestionIDs',
        searchData.data.map(d => d.id),
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private getUiParamsAndBody() {
    const uiParams = this.productsRepo.getState();

    // Check column sorting first, fall back to regular sorting
    const sortFilter: ISortBySearchParam =
      uiParams.columnSortBy && uiParams.columnSortByOrder
        ? { [uiParams.columnSortBy]: uiParams.columnSortByOrder }
        : { [uiParams.sortBy!]: uiParams.sortByOrder };

    const shortageStatuses = uiParams.shortageStatusFilter;

    const customers =
      uiParams.activeCustomersFilter.length === 0
        ? null
        : uiParams.activeCustomersFilter;

    const timePeriods =
      uiParams.activeSortalysTimePeriodsFilter.length === 0
        ? null
        : uiParams.activeSortalysTimePeriodsFilter;

    // setting dates for ease of use
    const startDate: Date | null = this.productsRepo.getStoreProp('startDate');
    const startISODate: string = startDate
      ? formatISO(startDate, { representation: 'date' })
      : '';

    const additionalInformation: ISearchQueryParams['additional_information'] =
      [];
    if (this.dataFieldsRepo.getState().otc)
      additionalInformation.push({ otc: true });
    if (this.dataFieldsRepo.getState().btm)
      additionalInformation.push({ btm: true });
    if (this.dataFieldsRepo.getState().tender)
      additionalInformation.push({ tender: true });

    // check if column sorting or dropdown sorting is available
    const hasSorting =
      (uiParams.columnSortBy && uiParams.columnSortByOrder) ||
      (uiParams.sortBy && uiParams.sortByOrder);

    const body = makeObjectWithNoNullValues({
      tab: productViewToTabNameMap.get(uiParams.productView)!,
      page: uiParams.currentPage,
      query: uiParams.searchTerm,
      sorts: hasSorting ? [sortFilter] : null,
      products: uiParams.selectedProduct ? [uiParams.selectedProduct] : null,
      manufacturers:
        this.manufacturersRepo.getSelectedManufacturersIds() || null,
      molecules: this.moleculesRepo.getSelectedMoleculesIds() || null,
      atcs: this.atcsRepo.getStoreProp('selectedAtcsIDs') || null,
      country: {
        id: (this.countryRepo.getActiveId() as number) || null,
      },
      out_of_shortage_dates: startISODate ? [{ from: startISODate }] : null,
      manufacturer_type: this.otherFiltersRepo.getState()
        .manufacturer_type_enabled
        ? this.otherFiltersRepo.getStoreProp('manufacturer_type')
        : null,
      expected_shortage_date:
        this.otherFiltersRepo.getStoreProp('expected_shortage_date') || null,
      additional_information: additionalInformation.length
        ? additionalInformation
        : null,
      statuses: shortageStatuses,
      customers,
      time_periods: timePeriods,
    });

    return { uiParams, body };
  }

  clearCurrentPageIDs(): void {
    this.productsRepo.setStoreProp('currentPageIDs', []);
  }

  private clearSuggestionIDs(): void {
    this.productsRepo.setStoreProp('suggestionIDs', []);
  }

  /**
   * Gets a date when product data was last changed.
   *
   * GET /countries/${country}/latest-date
   */
  getLatestUpdateDate(): Observable<{ latest_update_date: string }> {
    const country = this.countryRepo.getActiveId()?.toString() || null;

    return this.http
      .get<{
        latest_update_date: string;
      }>(environment.apiUrl + `/countries/${country}/latest-date`)
      .pipe(
        tap(data =>
          this.productsRepo.setStoreProp(
            'latestUpdateDate',
            data.latest_update_date,
          ),
        ),
      );
  }

  /**
   * Get a file with data from Sortalys export
   *
   * POST /products/export/sortalys
   *
   * @param exportType Type of file to export - XSLX only
   * @param exportAlternatives Whether to include alternative products
   * @returns Observable of ArrayBuffer containing the file data
   */
  getSortalysExport(
    exportType: FileTypes,
    exportAlternatives: boolean,
  ): Observable<ArrayBuffer> {
    const { body } = this.getUiParamsAndBody();

    return this.http
      .post(
        environment.apiUrl + `/products/export/sortalys`,
        {
          ...body,
          export_type: exportType,
          export_alternatives: exportAlternatives,
          export_tabs: [BackendViewNames.SORTALYS],
        },
        { responseType: 'arraybuffer' },
      )
      .pipe(
        catchError(error => {
          if (error?.error?.message?.includes('SORTALYS_HQ_EXPORT')) {
            throw new Error(
              'Sortalys export is not configured. Please contact your administrator.',
            );
          }
          throw error;
        }),
      );
  }

  /**
   * Get a file with data from regular export
   *
   * POST /products/export
   *
   * @param exportType Type of file to export - XSLX only
   * @param exportAlternatives Whether to include alternative products
   * @param tabs Array of tabs to include in the export
   * @returns Observable of ArrayBuffer containing the file data
   */
  getRegularExport(
    exportType: FileTypes,
    exportAlternatives: boolean,
    tabs: BackendViewNames[],
  ): Observable<ArrayBuffer> {
    const { body } = this.getUiParamsAndBody();

    return this.http.post(
      environment.apiUrl + `/products/export`,
      {
        ...body,
        export_type: exportType,
        export_alternatives: exportAlternatives,
        export_tabs: tabs,
      },
      { responseType: 'arraybuffer' },
    );
  }

  /**
   * Get file(s) with data from the currently active filters of products.
   * If Sortalys or ALL tab is selected, will return multiple files.
   *
   * @param exportType Type of file to export - XSLX only
   * @param exportAlternatives Whether to include alternative products
   * @param tabs Array of tabs to include in the export
   * @returns Observable of array of ArrayBuffers, where each ArrayBuffer is a file
   */
  getProductSearchExport(
    exportType: FileTypes,
    exportAlternatives: boolean,
    tabs: BackendViewNames[],
  ): Observable<ArrayBuffer[]> {
    const { uiParams } = this.getUiParamsAndBody();

    // If no tabs selected, use current view
    if (!tabs.length) {
      tabs = [productViewToTabNameMap.get(uiParams.productView)!];
    }

    // If current view is SORTALYS, only use sortalys endpoint
    if (uiParams.productView === ProductViews.SORTALYS) {
      return this.getSortalysExport(exportType, exportAlternatives).pipe(
        map(result => [result]),
      );
    }

    // If SORTALYS is one of multiple selected tabs or ALL is selected
    if (
      tabs.includes(BackendViewNames.SORTALYS) ||
      tabs.includes(BackendViewNames.ALL)
    ) {
      const nonSortalysTabs = tabs.filter(
        tab => tab !== BackendViewNames.SORTALYS,
      );

      // If ALL was selected, make sure it's included in the regular export
      if (
        tabs.includes(BackendViewNames.ALL) &&
        !nonSortalysTabs.includes(BackendViewNames.ALL)
      ) {
        nonSortalysTabs.push(BackendViewNames.ALL);
      }

      return combineLatest([
        this.getSortalysExport(exportType, exportAlternatives),
        this.getRegularExport(exportType, exportAlternatives, nonSortalysTabs),
      ]);
    }

    // Regular case - no SORTALYS involved
    return this.getRegularExport(exportType, exportAlternatives, tabs).pipe(
      map(result => [result]),
    );
  }

  /**
   * Method for search api
   */
  private search(): Observable<ISearchData | null> {
    this.cancelRequest.next();
    this.productsRepo.setLoading(true);

    const { body } = this.getUiParamsAndBody();

    return this.http
      .post<ISearchData>(environment.apiUrl + `/products`, body)
      .pipe(
        tap(data => {
          this.handleSearchData(data);
          this.productsRepo.setLoading(false);
        }),
        takeUntil(this.cancelRequest),
      );
  }

  private searchForSuggestions(): Observable<ISearchData | null> {
    this.cancelSuggestionRequest.next();
    const suggestionsSearchTerm = this.productsRepo.getStoreProp(
      'suggestionsSearchTerm',
    );

    if (
      !suggestionsSearchTerm ||
      suggestionsSearchTerm.length < DEFAULT_SEARCH_CHAR_LIMIT
    ) {
      // Added timeout to prevent issue with clearing suggestion IDs too fast after suggestion search term update
      setTimeout(() => {
        this.clearSuggestionIDs();
      }, 0);

      return just(null);
    }

    const { body } = this.getUiParamsAndBody();

    body['query'] = suggestionsSearchTerm;
    body['sorts'] = [{ is_sandoz: SortByOrder.DESC }];

    return this.http
      .post<ISearchData>(environment.apiUrl + `/products`, body)
      .pipe(
        tap(data => this.handleSuggestionsSearchData(data)),
        takeUntil(this.cancelSuggestionRequest),
      );
  }

  /**
   * Makes a search subscription and on `shouldFetch$` event calls `search()`
   */
  initSearchSubscription(): void {
    this.searchSubscription = this.shouldFetch$.subscribe(() => {
      // eslint-disable-next-line rxjs/no-nested-subscribe
      this.search().subscribe({
        error: (err: HttpErrorResponse) => {
          this.handleHttpError(err);
        },
      });

      // We also need to update the view counts anytime some filter changes,
      // so we also call getProductViewCounts here at the same time as the search
      // eslint-disable-next-line rxjs/no-nested-subscribe
      this.getProductViewCounts().subscribe();
    });
  }

  /**
   * Unsubscribes the `searchSubscription`
   */
  destroySearchSubscription(): void {
    this.cancelRequest.next();
    this.searchSubscription?.unsubscribe();
  }

  initSuggestionSearchSubscription(): void {
    this.suggestionsSearchSubscription = this.shouldFetchSuggestions$.subscribe(
      () => {
        // eslint-disable-next-line rxjs/no-nested-subscribe
        this.searchForSuggestions().subscribe({
          error: () => {
            this.clearSuggestionIDs();
          },
        });
      },
    );
  }

  destroySuggestionSearchSubscription(): void {
    this.cancelSuggestionRequest.next();
    this.suggestionsSearchSubscription?.unsubscribe();
  }

  initDisclaimerTextSubscription(): void {
    this.disclaimerTextSubscription = this.shouldFetchDisclaimer$.subscribe(
      // eslint-disable-next-line rxjs/no-nested-subscribe
      () => this.getProductDisclaimerText().subscribe(),
    );
  }

  destroyDisclaimerTextSubscription(): void {
    this.disclaimerTextSubscription?.unsubscribe();
  }

  /**
   * Create a order request for selected products.
   *
   * POST /product/stock-order
   *
   * @param productIDs
   */
  makeStockOrderOfProducts(productIDs: number[]): Observable<void> {
    const products: { id: number }[] = productIDs.map(id => ({ id }));

    this.productsRepo.setLoading(true);

    return this.http
      .post<void>(environment.apiUrl + `/product/stock-order`, { products })
      .pipe(
        tap(() => {
          // Update dates on products that the order was made on.
          // We do this so we don't have to refresh the page or re-query the api for this data.
          this.productsRepo.updateProducts(productIDs);

          this.productsRepo.setLoading(false);
        }),
      );
  }

  /**
   * Gets the disclaimer text for the product table.
   *
   * GET /translations/disclaimer?country={country_id}&language={language_id}
   */
  getProductDisclaimerText(): Observable<{ text: string }> {
    return this.http
      .get<{ text: string }>(environment.apiUrl + `/translations/disclaimer`)
      .pipe(
        tap(data =>
          this.productsRepo.setStoreProp('disclaimerText', data?.text),
        ),
      );
  }

  /**
   * Gets the table column config for the user
   *
   * GET /users/column-schemes/countries/{country}
   *
   * @param countryID
   */
  getTableColumnConfig(countryID: number): Observable<ITableColumnConfig[]> {
    this.productsRepo.setLoading(true);

    return this.http
      .get<ITableColumnConfig[]>(
        environment.apiUrl + `/users/column-schemes/countries/${countryID}`,
      )
      .pipe(
        tap(res => {
          emitOnce(() => {
            this.productsRepo.setStoreProp('tableColumnsConfig', res);
            this.productsRepo.setLoading(false);
          });
        }),
        catchError(err => {
          this.productsRepo.setLoading(false);

          return throwError(() => new Error(err));
        }),
      );
  }

  /**
   * Saves the column config
   *
   * PUT /users/column-schemes/{columnScheme}
   *
   * @param configID
   * @param config
   */
  saveTableColumnConfig(
    configID: ITableColumnConfig['id'],
    config: Omit<ITableColumnConfig, 'id' | 'tab_name'>,
  ): Observable<ITableColumnConfig> {
    return this.http
      .put<ITableColumnConfig>(
        environment.apiUrl + `/users/column-schemes/${configID}`,
        config,
      )
      .pipe(
        tap(res => {
          const currConfigArray = deepCopy(
            this.productsRepo.getStoreProp('tableColumnsConfig'),
          );
          const index = currConfigArray.findIndex(c => c.id === res.id);

          // Update the config array with the response
          currConfigArray.splice(index, 1, res);

          const user = this.sessionRepo.getStoreProp('authUser');
          const tableColumns = parseTableColumnConfig(
            currConfigArray,
            res.tab_name,
            this.countryRepo.getActiveCountry()!.country_code,
            user, // needed for checking if user is VIP because of Novelties columns
          );

          emitOnce(() => {
            this.productsRepo.setStoreProp(
              'tableColumnsConfig',
              currConfigArray,
            );
            this.productsRepo.setStoreProp('tableColumns', tableColumns);
            this.productsRepo.setStoreProp(
              'activeTableColumns',
              getActiveTableColumns(tableColumns),
            );
          });
        }),
      );
  }

  /**
   * Get the number counts for each product view
   *
   * GET /products/tab-counts
   */
  getProductViewCounts(): Observable<IProductViewsItemCounts> {
    const { body } = this.getUiParamsAndBody();

    return this.http
      .post<IProductViewsItemCounts>(
        environment.apiUrl + `/products/tab-counts`,
        body,
      )
      .pipe(
        tap(res =>
          this.productsRepo.setStoreProp('productViewsItemCounts', res),
        ),
      );
  }

  /**
   * Get the product.
   *
   * GET /products/{productMaster}
   *
   * @param productID
   */
  getProduct(productID: number): Observable<IProduct> {
    return this.http.get<IProduct>(
      environment.apiUrl + `/products/${productID}`,
    );
  }

  /**
   * Get the alternatives for product.
   *
   * GET /products/{productMaster}/alternatives
   *
   * @param productID
   */
  getProductAlternatives(
    productID: number,
  ): Observable<IProduct['alternatives']> {
    return this.http.get<IProduct['alternatives']>(
      environment.apiUrl + `/products/${productID}/alternatives`,
    );
  }

  /**
   * Not an index. Actual page number.
   * @param currentPage page number
   */
  setPage(currentPage: number): void {
    if (currentPage > this.productsRepo.getStoreProp('totalPages')!) return;

    this.productsRepo.setStoreProp('currentPage', currentPage);
  }

  resetStore(): void {
    // a way to exclude resetting data from the store
    const { latestUpdateDate, tableColumnsConfig } =
      this.productsRepo.getState();
    this.productsRepo.resetStore();

    this.productsRepo.setStoreProp('latestUpdateDate', latestUpdateDate);
    this.productsRepo.setStoreProp('tableColumnsConfig', tableColumnsConfig);
  }

  startDownloading(): void {
    this.downloading$.next(true);
  }

  stopDownloading(): void {
    this.downloading$.next(false);
  }

  isDownloading$(): Observable<boolean> {
    return this.downloading$.pipe(
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }
}
